export type PublicEnvironmentName =
  | "VITE_API_BASE_URL"
  | "VITE_CHAIN_RPC_URL"
  | "VITE_CHAIN_ID"
  | "VITE_PRELAUNCH"
  | "VITE_REOWN_PROJECT_ID"
  | "VITE_BROKER_NFT_ADDRESS"
  | "VITE_LAND_TOKEN_ADDRESS"
  | "VITE_ACTIVATION_MANAGER_ADDRESS"
  | "VITE_TOKEN_ESCROW_ADDRESS"
  | "VITE_STOCK_BOOSTER_ADDRESS"
  | "VITE_OVERTIME_BOOSTER_ADDRESS"
  | "VITE_LOAN_VAULT_ADDRESS"
  | "VITE_BROKER_AMM_ADDRESS"
  | "VITE_ART_BASE_URL"
  | "VITE_UNISWAP_V4_POOL_MANAGER_ADDRESS"
  | "VITE_UNISWAP_V4_STATE_VIEW_ADDRESS"
  | "VITE_UNISWAP_V4_QUOTER_ADDRESS"
  | "VITE_UNISWAP_V4_UNIVERSAL_ROUTER_ADDRESS"
  | "VITE_UNISWAP_PERMIT2_ADDRESS"
  | "VITE_UNISWAP_V4_POOL_ID"
  | "VITE_UNISWAP_V4_TICK_SPACING"
  | "VITE_UNISWAP_V4_HOOKS_ADDRESS";

declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL?: string;
    readonly VITE_CHAIN_RPC_URL?: string;
    readonly VITE_CHAIN_ID?: string;
    readonly VITE_PRELAUNCH?: string;
    readonly VITE_REOWN_PROJECT_ID?: string;
    readonly VITE_BROKER_NFT_ADDRESS?: string;
    readonly VITE_LAND_TOKEN_ADDRESS?: string;
    readonly VITE_ACTIVATION_MANAGER_ADDRESS?: string;
    readonly VITE_TOKEN_ESCROW_ADDRESS?: string;
    readonly VITE_STOCK_BOOSTER_ADDRESS?: string;
    readonly VITE_OVERTIME_BOOSTER_ADDRESS?: string;
    readonly VITE_LOAN_VAULT_ADDRESS?: string;
    readonly VITE_BROKER_AMM_ADDRESS?: string;
    readonly VITE_ART_BASE_URL?: string;
    readonly VITE_UNISWAP_V4_POOL_MANAGER_ADDRESS?: string;
    readonly VITE_UNISWAP_V4_STATE_VIEW_ADDRESS?: string;
    readonly VITE_UNISWAP_V4_QUOTER_ADDRESS?: string;
    readonly VITE_UNISWAP_V4_UNIVERSAL_ROUTER_ADDRESS?: string;
    readonly VITE_UNISWAP_PERMIT2_ADDRESS?: string;
    readonly VITE_UNISWAP_V4_POOL_ID?: string;
    readonly VITE_UNISWAP_V4_TICK_SPACING?: string;
    readonly VITE_UNISWAP_V4_HOOKS_ADDRESS?: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// Keep this allowlist explicit. Reading the complete `import.meta.env` object makes
// Vite serialize every VITE_* value it finds, including retired network settings.
const viteEnvironment: Partial<Record<PublicEnvironmentName, string>> = {
  VITE_API_BASE_URL: import.meta.env?.VITE_API_BASE_URL,
  VITE_CHAIN_RPC_URL: import.meta.env?.VITE_CHAIN_RPC_URL,
  VITE_CHAIN_ID: import.meta.env?.VITE_CHAIN_ID,
  VITE_PRELAUNCH: import.meta.env?.VITE_PRELAUNCH,
  VITE_REOWN_PROJECT_ID: import.meta.env?.VITE_REOWN_PROJECT_ID,
  VITE_BROKER_NFT_ADDRESS: import.meta.env?.VITE_BROKER_NFT_ADDRESS,
  VITE_LAND_TOKEN_ADDRESS: import.meta.env?.VITE_LAND_TOKEN_ADDRESS,
  VITE_ACTIVATION_MANAGER_ADDRESS:
    import.meta.env?.VITE_ACTIVATION_MANAGER_ADDRESS,
  VITE_TOKEN_ESCROW_ADDRESS: import.meta.env?.VITE_TOKEN_ESCROW_ADDRESS,
  VITE_STOCK_BOOSTER_ADDRESS: import.meta.env?.VITE_STOCK_BOOSTER_ADDRESS,
  VITE_OVERTIME_BOOSTER_ADDRESS:
    import.meta.env?.VITE_OVERTIME_BOOSTER_ADDRESS,
  VITE_LOAN_VAULT_ADDRESS: import.meta.env?.VITE_LOAN_VAULT_ADDRESS,
  VITE_BROKER_AMM_ADDRESS: import.meta.env?.VITE_BROKER_AMM_ADDRESS,
  VITE_ART_BASE_URL: import.meta.env?.VITE_ART_BASE_URL,
  VITE_UNISWAP_V4_POOL_MANAGER_ADDRESS:
    import.meta.env?.VITE_UNISWAP_V4_POOL_MANAGER_ADDRESS,
  VITE_UNISWAP_V4_STATE_VIEW_ADDRESS:
    import.meta.env?.VITE_UNISWAP_V4_STATE_VIEW_ADDRESS,
  VITE_UNISWAP_V4_QUOTER_ADDRESS:
    import.meta.env?.VITE_UNISWAP_V4_QUOTER_ADDRESS,
  VITE_UNISWAP_V4_UNIVERSAL_ROUTER_ADDRESS:
    import.meta.env?.VITE_UNISWAP_V4_UNIVERSAL_ROUTER_ADDRESS,
  VITE_UNISWAP_PERMIT2_ADDRESS:
    import.meta.env?.VITE_UNISWAP_PERMIT2_ADDRESS,
  VITE_UNISWAP_V4_POOL_ID: import.meta.env?.VITE_UNISWAP_V4_POOL_ID,
  VITE_UNISWAP_V4_TICK_SPACING:
    import.meta.env?.VITE_UNISWAP_V4_TICK_SPACING,
  VITE_UNISWAP_V4_HOOKS_ADDRESS:
    import.meta.env?.VITE_UNISWAP_V4_HOOKS_ADDRESS,
};

export function publicEnvironment(name: PublicEnvironmentName) {
  const runtimeValue =
    typeof process === "undefined" ? undefined : process.env[name];
  const buildValue = viteEnvironment[name];
  return runtimeValue ?? (typeof buildValue === "string" ? buildValue : undefined);
}
